import {Component, OnInit} from '@angular/core';
import {NgClass} from '@angular/common';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';

import {Observable, timer} from 'rxjs';

import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DeviceDetectorService} from 'ngx-device-detector';

import {AUTHService, PAYMENTService} from 'finbricks-onebank-public-api/api/api';
import {
  CancelPaymentRequest,
  ConfirmAuthRequest, ConfirmAuthResponse,
  ConfirmPaymentRequest, ConfirmPaymentResponse,
  CancelAuthRequest, CancelAuthResponse,
  Psu,
  ModelError
} from 'finbricks-onebank-public-api/model/models';

interface RedirectResponse {
  /**
   * Redirect URI
   */
  redirectUri?: string;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    NgClass
  ],
  templateUrl: './confirm.component.html'
})
export class ConfirmComponent implements OnInit {

  checkoutForm = this.formBuilder.group({
    sms: ['', Validators.required]
  });

  protected bankTitle = '';
  private readonly bank365Title = '365.bank';
  private bank: string | undefined;
  private readonly bank365 = 'BANK365_SK';

  private authId: string | undefined;

  // It should be 5 minutes 1000 * 60 * 5
  private readonly delayForCancel = 1000 * 60 * 5;

  constructor(private translateService: TranslateService, private route: ActivatedRoute,
              private authService: AUTHService, private paymentService: PAYMENTService,
              private formBuilder: FormBuilder, private deviceService: DeviceDetectorService) {
    this.translateService.use('sk');
  }

  ngOnInit(): void {
    this.bank = this.route.snapshot.queryParams['bank'];
    if (this.bank && this.bank === this.bank365) {
      this.bankTitle = this.bank365Title;
    }
    this.authId = this.route.snapshot.queryParams['authId'];
    timer(this.delayForCancel).subscribe(() => this.cancel());
  }

  cancel() {
    let response$: Observable<CancelAuthResponse>;
    if (this.authId) {
      response$ = this.cancelAuth();
    } else {
      response$ = this.cancelPayment();
    }
    this.processSubscibe(response$);
  }

  onSubmit(): void {
    const xRequestId = crypto.randomUUID();

    let response$: Observable<ConfirmAuthResponse | ConfirmPaymentResponse>;
    if (this.authId) {
      response$ = this.confirmAuth(xRequestId);
    } else {
      response$ = this.confirmPayment(xRequestId);
    }
    this.processSubscibe(response$);
  }

  private processSubscibe<T extends RedirectResponse>(response$: Observable<T>): void {
    response$.subscribe({
      next: value => {
        const redirectUri = value.redirectUri;
        if (redirectUri) {
          this.checkoutForm.reset();
          location.replace(redirectUri);
        }
      },
      error: err => {
        console.error(err);
        const title = ['Errors:', '---------'];
        const mappedEerrorCodeAndMessage = err.error.map((item: ModelError) => 'code: ' + item.code + '    message: ' + item.message);
        const codesAndMessages = [...title, mappedEerrorCodeAndMessage];
        alert(codesAndMessages.join('\n'));
      }
    });
  }

  private confirmAuth(xRequestId: string): Observable<ConfirmAuthResponse> {
    const confirmAuthRequest = {
      code: this.checkoutForm.value.sms,
      type: this.route.snapshot.queryParams['type'],
      authId: this.authId,
      bank: this.bank,
      psu: {
        userAgent: window.navigator.userAgent,
        ipAddress: location.host,
        ipPort: location.port,
        deviceOs: this.deviceService.os
      } as Psu
    } as ConfirmAuthRequest;
    return this.authService.authConfirm(xRequestId, confirmAuthRequest);
  }

  private confirmPayment(xRequestId: string): Observable<ConfirmPaymentResponse> {
    const confirmPaymentRequest = {
      code: this.checkoutForm.value.sms,
      type: this.route.snapshot.queryParams['type'],
      paymentId: this.route.snapshot.queryParams['paymentId'],
      bank: this.bank,
      psu: {
        userAgent: window.navigator.userAgent,
        ipAddress: location.host,
        ipPort: location.port,
        deviceOs: this.deviceService.os
      } as Psu
    } as ConfirmPaymentRequest;
    return this.paymentService.paymentConfirm(xRequestId, confirmPaymentRequest);
  }

  private cancelAuth(): Observable<CancelAuthResponse> {
    return this.authService.cancelAuth({bank: this.bank, authId: this.authId} as CancelAuthRequest);
  }

  private cancelPayment(): Observable<CancelAuthResponse> {
    const paymentId = this.route.snapshot.queryParams['paymentId'];
    return this.paymentService.cancelPayment({bank: this.bank, paymentId} as CancelPaymentRequest);
  }
}
